/**
 * Typed client for the Pillars tab: pillar rankings, their backtests, the pairs scan.
 *
 * Re-weighting is a request, not arithmetic here, on purpose: the blend lives once,
 * in Python, where the backtest uses the same function. A second copy in TypeScript
 * would be a second thing that has to agree with it.
 */
import { get } from "./api";

export const PILLARS = ["value", "quality", "growth", "momentum", "risk"] as const;
export type Pillar = (typeof PILLARS)[number];
export type Weights = Record<Pillar, number>;

export interface PillarMeta {
  pillars: { name: Pillar; factors: { name: string; direction: number; description: string }[] }[];
  presets: Record<string, Weights>;
  sectors: string[];
  sizes: string[];
  /** Today's constituents — a view filter for the latest date only. */
  indexes: { key: string; label: string; n: number; as_of: string }[];
  panel: { start: string; end: string; n_dates: number; n_rows: number; built_at: string } | null;
  dates: string[];
  prices_through: string | null;
  top_frac: number;
  cost_bps: number;
  min_pillars: number;
}

export type PillarRow = {
  /** Rank within the current view (after filters); `rank_all` is across the universe. */
  rank: number;
  rank_all: number;
  prev_rank: number | null;
  cik: number;
  ticker: string;
  title: string | null;
  ff12: string;
  score: number;
} & Record<Pillar, number | null>;

export type SectorRow = {
  ff12: string;
  n: number;
  n_top: number;
  share_universe: number;
  share_top: number;
} & Record<Pillar, number | null>;

export interface PillarRank {
  on: string;
  prev_on: string | null;
  weights: Weights;
  n_ranked: number;
  n_top: number;
  index_as_of: string | null;
  rows: PillarRow[];
  sectors: SectorRow[];
  elapsed_ms: number;
}

export interface IcWeights {
  as_of: string;
  current: Weights;
  n_obs: number;
  lookback: number;
  min_history: number;
  series: ({ date: string; n_obs: number } & Weights)[];
}

export interface Backtest {
  label: string;
  weights: Weights | null;
  adaptive: boolean;
  top_frac: number;
  cost_bps: number;
  window: { start: string; end: string };
  stats: Record<string, number>;
  pillar_ic: { pillar: Pillar; mean_ic: number; t: number; hit: number; n: number }[];
  ic_series: { date: string; ic: number }[];
  quantiles: { q: number; mean_fwd_return: number }[];
  monthly: { period: string; model: number; universe: number; market: number | null; turnover: number }[];
  quarterly: { quarter: string; model: number; universe: number; market: number | null }[];
  alpha: { quarter: string; beat: number; lagged: number; net: number }[];
  attribution: {
    quarters: string[];
    names: string[];
    cells: { name: string; quarter: string; excess: number }[];
  };
  /** Never read a backtest without this: how many weightings have been tried, and
   *  the t-stat the best of that many noise trials would show. */
  luck: { configurations_tried: number; benchmark_t: number; beats: boolean };
  elapsed_ms: number;
}

export interface PairRow {
  a: string;
  b: string;
  sector_a: string | null;
  sector_b: string | null;
  p_value: number;
  t_stat: number;
  half_life: number | null;
  corr: number | null;
  hedge_ratio: number;
  z: number | null;
  oos_p: number | null;
  fdr: boolean;
  signal: "long_spread" | "short_spread" | "watch" | "flat" | "unhedged" | "diverged";
}

export interface PairScan {
  on: string;
  mode: string;
  n_requested: number;
  n_names: number;
  n_dropped: number;
  n_tests: number;
  n_significant: number;
  expected_by_chance: number;
  n_fdr: number;
  alpha: number;
  formation_bars: number;
  holdout_bars: number;
  z_entry: number;
  z_exit: number;
  pairs: PairRow[];
  elapsed_ms: number;
}

export interface PairTest extends Omit<PairRow, "sector_a" | "sector_b" | "fdr"> {
  on: string;
  holdout_from: string;
  series: { date: string; z: number }[];
}

export interface ExportsManifest {
  built_at: string;
  /** Date of the newest SEC filing the store holds — how current the fundamentals are. */
  fundamentals_through: string;
  files: {
    name: string;
    title: string;
    description: string;
    rows: number;
    companies: number;
    bytes: number;
    from: string;
    to: string;
    columns: { name: string; description: string }[];
  }[];
  by_year: {
    year: number;
    month_ends: number | null;
    companies_ranked: number | null;
    avg_per_month: number | null;
    companies_with_fundamentals: number | null;
  }[];
}

export interface ViewFilters {
  sector: string;
  size: string;
  index: string;
}

export function weightsParam(w: Weights): string {
  return PILLARS.map((p) => `${p}:${w[p]}`).join(",");
}

function filterParams(f: ViewFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.sector) out.sector = f.sector;
  if (f.size) out.size = f.size;
  if (f.index) out.index = f.index;
  return out;
}

export const pillarsApi = {
  meta: () => get<PillarMeta>("/pillars/meta"),
  /** Rows travel as arrays under a shared `columns` header and are rebuilt here. */
  rank: async (on: string, w: Weights, f: ViewFilters): Promise<PillarRank> => {
    type Wire = Omit<PillarRank, "rows"> & { columns: string[]; rows: unknown[][] };
    const res = await get<Wire>("/pillars/rank", { on, w: weightsParam(w), ...filterParams(f) });
    const { columns, rows, ...rest } = res;
    return {
      ...rest,
      rows: rows.map((r) => Object.fromEntries(columns.map((c, i) => [c, r[i]])) as PillarRow),
    };
  },
  icWeights: () => get<IcWeights>("/pillars/ic-weights"),
  backtest: (w: Weights | null) =>
    get<Backtest>("/pillars/backtest", w ? { w: weightsParam(w) } : { adaptive: true }),
  pairsScan: (mode: string, f: ViewFilters, maxNames: number) =>
    get<PairScan>("/pairs/scan", { mode, max_names: maxNames, ...filterParams(f) }),
  pairsTest: (a: string, b: string) => get<PairTest>("/pairs/test", { a, b }),
  exports: () => get<ExportsManifest>("/exports"),
};

/** One colour per Fama-French 12 industry, stable across every chart on the tab. */
export const SECTOR_COLORS: Record<string, string> = {
  NoDur: "#26a69a",
  Durbl: "#ff8a65",
  Manuf: "#b0bec5",
  Enrgy: "#ba68c8",
  Chems: "#66bb6a",
  BusEq: "#4dd0e1",
  Telcm: "#ffca28",
  Utils: "#90a4ae",
  Shops: "#f06292",
  Hlth: "#ffa726",
  Money: "#4c8dff",
  Other: "#8d6e63",
};

export const PILLAR_COLORS: Record<Pillar, string> = {
  value: "#ffca28",
  quality: "#26a69a",
  growth: "#66bb6a",
  momentum: "#4c8dff",
  risk: "#ef5350",
};

export const pct = (v: number | null | undefined, digits = 1): string =>
  v == null ? "—" : `${(v * 100).toFixed(digits)}%`;
export const signedPct = (v: number | null | undefined, digits = 1): string =>
  v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(digits)}%`;
export const num = (v: number | null | undefined, digits = 2): string =>
  v == null ? "—" : v.toFixed(digits);
export const signed = (v: number | null | undefined, digits = 2): string =>
  v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(digits)}`;
